import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";
import { Oppenvardsbesok } from "@/tabs/Oppenvard/types";
import { klinikenApi, getConfig } from "@/api";

type State = BaseState & {
    oppenvardsbesok: Map<Oppenvardsbesok["id"], Oppenvardsbesok>;
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        oppenvardsbesok: new Map<Oppenvardsbesok["id"], Oppenvardsbesok>(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "oppenvardsbesok";
const apiUrl = "oppenvard/oppenvardsbesok/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        oppenvardsbesok: (state: State) => [...state.oppenvardsbesok.values()],
        oppenvardsbesokSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: moduleName,
            }),
        oppenvardsbesokById:
            (state: State) =>
            ({ id }: { id?: string }) => {
                if (!id) {
                    return null;
                }

                return state.oppenvardsbesok.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addOppenvardsbesok: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                oppenvardsbesok = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                oppenvardsbesok: Oppenvardsbesok[];
            }
        ) => {
            subsetSetter<State, Oppenvardsbesok>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: oppenvardsbesok,
            });
        },
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchOppenvardsbesok: async (
            { commit, state }: BaseActionsProps<State>,
            {
                filterParams = {},
                config = {},
                limit = 20,
                page = 1,
                resetLocalSubsets,
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                limit: number;
                page: number;
                resetLocalSubsets?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Oppenvardsbesok>({
                    filterParams,
                    state,
                    config,
                    page,
                    limit,
                    collectionName: moduleName,
                    apiUrl,
                });

                if (resetLocalSubsets) {
                    commit("resetLocalSubsets");
                }

                if (typeof total === "number") {
                    commit("setTotal", {
                        total,
                        subsetName,
                    });
                }

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addOppenvardsbesok", {
                    subsetName,
                    offset: resultOffset,
                    oppenvardsbesok: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    data: results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        async direktboka(_: BaseActionsProps<State>, { payload, config }: { payload: any; config?: any }) {
            try {
                return await klinikenApi.post("oppenvard/direktboka/", payload, getConfig(config));
            } catch (error) {
                console.error("Fel vid direktbokning");
                throw error;
            }
        },
    },
};
